import root from 'window-or-global';

import env from 'env/env';
import locator from 'utils/locator';
import { session } from 'utils/storage';
import { consoleWarn } from 'utils/logging';

export type SessionProps = {
  branch_id?: string;
  content_group?: string;
  customer_id?: string | number | null;
  instant_checkout_availability_reason?: string;
  instant_checkout_available?: boolean;
  instant_checkout_eligibility?: boolean;
  mywaitrose_member?: boolean;
  order_id?: string;
  page_location?: string;
  page_path?: string;
  page_title?: string;
  page_type?: string;
  partner_discount_status?: string;
  previous_path?: string;
  purchase_or_amend?: string;

  // user_id is set from customer_id
  user_id?: string | number;

  // These properties are not common session properties but are set for later GTM events to use
  service_type?: string;
  payment_type?: string;
};

export type AnalyticsIdentifiers = {
  clientId?: string;
  sessionId?: string;
};

export type AnalyticsConfig = AnalyticsIdentifiers & {
  props?: SessionProps;
};

type AnalyticsState = {
  enabled: boolean;
  loading: boolean;
  loaded: boolean;
} & AnalyticsConfig;

export type AnalyticsStore = {
  subscribe(callback: () => void): () => void;
  getSnapshot(): AnalyticsConfig;
};

const AnalyticsSessionStateId = 'wtr-analytics';

export function getSessionState(): AnalyticsConfig {
  let gaSettings: AnalyticsConfig | undefined;
  try {
    gaSettings = session.getJson<AnalyticsConfig>(AnalyticsSessionStateId);
  } catch (_error) {
    consoleWarn('Failed to get analytics session state', _error);
  }
  return gaSettings ?? {};
}

export function setSessionState(value: Partial<AnalyticsConfig>) {
  const gaSettings = getSessionState();
  session.setJson(AnalyticsSessionStateId, { ...gaSettings, ...value });
}

export function clearSessionState() {
  const existingState = getSessionState();
  session.remove(AnalyticsSessionStateId);
  session.setJson(AnalyticsSessionStateId, {
    clientId: existingState.clientId,
    sessionId: existingState.sessionId,
  });
}

function isFunction(value: unknown | undefined): value is (...args: unknown[]) => void {
  return !!value && typeof value === 'function';
}
function isWindow(value: typeof root): value is typeof window {
  // eslint-disable-next-line no-underscore-dangle
  return !root.__SERVER__ && value === root;
}

export const googleAnalyticsStore = (analyticsEnabled: boolean): AnalyticsStore => {
  let listeners: (() => void)[] = [];

  let clientId: string | undefined;
  let sessionId: string | undefined;

  // Mobile Apps may pass clientId and sessionId in the URL
  const queryParams = new URLSearchParams(locator.search);
  clientId = queryParams.get('ga_cid') || undefined;
  sessionId = queryParams.get('ga_session_id') || undefined;

  const sessionState = getSessionState();
  if (!clientId && sessionState.clientId) {
    clientId = sessionState.clientId;
  }
  if (!sessionId && sessionState.sessionId) {
    sessionId = sessionState.sessionId;
  }

  let state: AnalyticsState = {
    enabled: analyticsEnabled,
    loading: false,
    loaded: false,
    clientId,
    sessionId,
  };

  const stateChange = (change: Partial<AnalyticsState> = {}) => {
    state = { ...state, ...change };
    // We only care about notifying when we have both clientId and sessionId
    if (state.enabled && state.loaded && state.clientId && state.sessionId) {
      listeners.forEach(listener => listener());
    }
  };

  const fetchIds = () => {
    const gaSettings = getSessionState();
    if (gaSettings.clientId && gaSettings.sessionId) {
      stateChange({
        loaded: true,
        clientId: gaSettings.clientId,
        sessionId: gaSettings.sessionId,
      });
      return;
    }

    if (!state.loading && !state.loaded) {
      stateChange({ loading: true });

      // We want to use the real dataLayer rather than the async proxy
      // eslint-disable-next-line no-restricted-properties
      root.dataLayer?.push(() => {
        // isWindow check for TypeScript type guard so we can see gtag function
        if (state.loaded || !isWindow(root)) {
          return;
        }

        stateChange({ loading: false, loaded: isFunction(root.gtag) });

        // Repeated type guard check to ensure gtag is a function
        if (isFunction(root.gtag)) {
          if (!state.clientId) {
            root.gtag('get', env.googleAnalytics.measurementId, 'client_id', (id: string) => {
              setSessionState({ clientId: id });
              stateChange({ clientId: id });
            });
          }
          if (!state.sessionId) {
            root.gtag('get', env.googleAnalytics.measurementId, 'session_id', (id: string) => {
              setSessionState({ sessionId: id });
              stateChange({ sessionId: id });
            });
          }
        }
      });
    }
  };

  return {
    subscribe(callback: () => void) {
      if (__SERVER__) {
        return () => {};
      }
      listeners = [...listeners, callback];

      if (state.enabled && !state.loading && !state.loaded) {
        fetchIds();
      }

      return () => {
        listeners = listeners.filter(l => l !== callback);
      };
    },

    getSnapshot() {
      return state;
    },
  };
};
